export interface WizardCountryOption {
  cfId?: number;
  name: string;
  imageUrl?: string;
  imageAltTag?: string;
}

export interface WizardOption {
  cfId?: number;
  label: string;
  imageUrl?: string;
  imageAltTag?: string;
  materialIcon?: string;
}

export interface WizardOptions {
  countries: WizardCountryOption[];
  degreeLevels: WizardOption[];
  studyAreas: WizardOption[];
  cities: WizardOption[];
  nearestOffices: WizardOption[];
  coachingOptions: WizardOption[];
}

// Last-resort fallbacks used when the CMS / Frappe source returns empty.
// Keeps the wizard usable during outages; CMS data still wins when available.
// Snapshot of CMS pref-countries-lists (sortOrder ASC) as of 2026-05-15.
export const FALLBACK_COUNTRIES: WizardCountryOption[] = [
  { cfId: 1,   name: "United States",        imageUrl: "https://assets.studies-overseas.com/usaflag_d54c445770.svg" },
  { cfId: 2,   name: "Australia",            imageUrl: "https://assets.studies-overseas.com/australiaflag_3d98752af7.svg" },
  { cfId: 3,   name: "Canada",               imageUrl: "https://assets.studies-overseas.com/canada_a566b56bf5.svg" },
  { cfId: 4,   name: "United Kingdom",       imageUrl: "https://assets.studies-overseas.com/unitedkingdom_64a4833290.svg" },
  { cfId: 5,   name: "New Zealand",          imageUrl: "https://assets.studies-overseas.com/newzealand_a09e3fae3c.svg" },
  { cfId: 6,   name: "Singapore",            imageUrl: "https://flagcdn.com/sg.svg" },
  { cfId: 7,   name: "United Arab Emirates", imageUrl: "https://flagcdn.com/ae.svg" },
  {            name: "Europe",               imageUrl: "https://assets.studies-overseas.com/europeanunion_9c5873e410.svg" },
  { cfId: 8,   name: "Ireland",              imageUrl: "https://assets.studies-overseas.com/irelands_b8599fe6d4.svg" },
  {            name: "Asia",                 imageUrl: "https://assets.studies-overseas.com/Dubai_Flag_dd19eef948.jpg" },
  { cfId: 9,   name: "Germany",              imageUrl: "https://flagcdn.com/de.svg" },
  {            name: "Turkey",               imageUrl: "https://flagcdn.com/tr.svg" },
  { cfId: 10,  name: "France",               imageUrl: "https://flagcdn.com/fr.svg" },
  { cfId: 11,  name: "Sweden",               imageUrl: "https://flagcdn.com/se.svg" },
  { cfId: 12,  name: "Netherlands",          imageUrl: "https://flagcdn.com/nl.svg" },
  { cfId: 13,  name: "Austria",              imageUrl: "https://flagcdn.com/at.svg" },
  { cfId: 14,  name: "Denmark",              imageUrl: "https://flagcdn.com/dk.svg" },
  { cfId: 15,  name: "Finland",              imageUrl: "https://flagcdn.com/fi.svg" },
  { cfId: 16,  name: "Italy",                imageUrl: "https://flagcdn.com/it.svg" },
  { cfId: 17,  name: "Hungary",              imageUrl: "https://flagcdn.com/hu.svg" },
  { cfId: 18,  name: "Switzerland",          imageUrl: "https://flagcdn.com/ch.svg" },
  { cfId: 19,  name: "Spain",                imageUrl: "https://flagcdn.com/es.svg" },
  { cfId: 20,  name: "Lithuania",            imageUrl: "https://flagcdn.com/lt.svg" },
  { cfId: 21,  name: "Latvia",               imageUrl: "https://flagcdn.com/lv.svg" },
  { cfId: 22,  name: "Cyprus",               imageUrl: "https://flagcdn.com/cy.svg" },
  { cfId: 23,  name: "Poland",               imageUrl: "https://flagcdn.com/pl.svg" },
  { cfId: 24,  name: "Czech Republic",       imageUrl: "https://flagcdn.com/cz.svg" },
  { cfId: 25,  name: "Malaysia",             imageUrl: "https://flagcdn.com/my.svg" },
  { cfId: 26,  name: "Mauritius",            imageUrl: "https://flagcdn.com/mu.svg" },
  { cfId: 27,  name: "South Africa",         imageUrl: "https://flagcdn.com/za.svg" },
  { cfId: 28,  name: "Hong Kong",            imageUrl: "https://flagcdn.com/hk.svg" },
  { cfId: 32,  name: "China",                imageUrl: "https://flagcdn.com/cn.svg" },
  { cfId: 33,  name: "Bulgaria",             imageUrl: "https://flagcdn.com/bg.svg" },
  { cfId: 35,  name: "Vietnam",              imageUrl: "https://flagcdn.com/vn.svg" },
  { cfId: 37,  name: "Malta",                imageUrl: "https://flagcdn.com/mt.svg" },
  { cfId: 38,  name: "Japan",                imageUrl: "https://flagcdn.com/jp.svg" },
  { cfId: 39,  name: "Belgium",              imageUrl: "https://flagcdn.com/be.svg" },
  { cfId: 40,  name: "Russia",               imageUrl: "https://flagcdn.com/ru.svg" },
  { cfId: 41,  name: "South Korea",          imageUrl: "https://flagcdn.com/kr.svg" },
  { cfId: 89,  name: "India",                imageUrl: "https://flagcdn.com/in.svg" },
  { cfId: 110, name: "Indonesia",            imageUrl: "https://flagcdn.com/id.svg" },
  { cfId: 46,  name: "Georgia",              imageUrl: "https://flagcdn.com/ge.svg" },
  { cfId: 94,  name: "Zimbabwe",             imageUrl: "https://flagcdn.com/zw.svg" },
];

// Snapshot of CMS pref-study-areas (sortOrder ASC) as of 2026-05-15.
export const FALLBACK_STUDY_AREAS: WizardOption[] = [
  { cfId: 1,  label: "Agriculture, Forestry and Fishery" },
  { cfId: 2,  label: "Architecture and Building" },
  { cfId: 3,  label: "Arts" },
  { cfId: 4,  label: "Commerce, Business and Administration" },
  { cfId: 5,  label: "Computer Science and Information Technology" },
  { cfId: 6,  label: "Education" },
  { cfId: 7,  label: "Engineering and Engineering Trades" },
  { cfId: 8,  label: "Environmental Science/Protection" },
  { cfId: 9,  label: "Health" },
  { cfId: 10, label: "Humanities" },
  { cfId: 11, label: "Journalism and Information" },
  { cfId: 12, label: "Law" },
  { cfId: 13, label: "Life Sciences" },
  { cfId: 14, label: "Manufacturing and Processing" },
  { cfId: 15, label: "Mathematics and Statistics" },
  { cfId: 16, label: "Personal Services" },
  { cfId: 17, label: "Physical Sciences, Sciences" },
  { cfId: 18, label: "Security Services" },
  { cfId: 19, label: "Social and Behavioural Science" },
  { cfId: 20, label: "Social Services" },
  { cfId: 21, label: "Transport Services" },
  { cfId: 22, label: "Veterinary" },
];

// Fallback city list for the wizard "Which city do you live in?" step.
// Used only when Frappe `get_cities_for_b2c` returns empty or fails.
// Refresh manually by re-syncing from product's master list.
export const FALLBACK_CITIES: WizardOption[] = [
  { label: "Achalpur" },
  { label: "Adilabad" },
  { label: "Agra" },
  { label: "Ahilyanagar" },
  { label: "Ahmedabad" },
  { label: "Ahmednagar" },
  { label: "Akola" },
  { label: "Akurdi" },
  { label: "Alappuzha" },
  { label: "Allahabad" },
  { label: "Amarkantak" },
  { label: "Amravati" },
  { label: "Amritsar" },
  { label: "Anand" },
  { label: "Anantapur" },
  { label: "Aurangabad" },
  { label: "Badnera" },
  { label: "Balaghat" },
  { label: "Balco" },
  { label: "Balharshah" },
  { label: "Ballarpur" },
  { label: "Bangalore" },
  { label: "Bareilli" },
  { label: "Beed" },
  { label: "Belgaum" },
  { label: "Betul" },
  { label: "Bhadravati" },
  { label: "Bhandara" },
  { label: "Bhatapara" },
  { label: "Bhavnagar" },
  { label: "Bhilai" },
  { label: "Bhopal" },
  { label: "Bhubaneshwar" },
  { label: "Bhusawal" },
  { label: "Bilaspur" },
  { label: "Buldhana" },
  { label: "Calicut" },
  { label: "Ch. Sambhajinagar" },
  { label: "Champa" },
  { label: "Chandigarh" },
  { label: "Chandpur" },
  { label: "Chandrapur" },
  { label: "Chennai" },
  { label: "Chhindwara" },
  { label: "Chimur" },
  { label: "Chittoor" },
  { label: "Chittorgarh" },
  { label: "Coimbatore" },
  { label: "Daryapur Banosa" },
  { label: "Dehradun" },
  { label: "Delhi" },
  { label: "Dhule" },
  { label: "Dungarpur" },
  { label: "Durg" },
  { label: "Edakkara" },
  { label: "Ernakulam" },
  { label: "Faridabad" },
  { label: "Gadchiroli" },
  { label: "Gandhinagar" },
  { label: "Ghaziabad" },
  { label: "Ghugus" },
  { label: "Goa" },
  { label: "Gondia" },
  { label: "Greater Noida" },
  { label: "Guntur" },
  { label: "Gurugram" },
  { label: "Gwalior" },
  { label: "Hanamkonda" },
  { label: "Haridwar" },
  { label: "Hinganghat" },
  { label: "Hubli" },
  { label: "Hyderabad" },
  { label: "Idukki" },
  { label: "Indore" },
  { label: "Jabalpur" },
  { label: "Jaipur" },
  { label: "Jalandhar" },
  { label: "Jalgaon" },
  { label: "Jalna" },
  { label: "Jammu" },
  { label: "Jamnipali" },
  { label: "Jodhpur" },
  { label: "Kadappa" },
  { label: "Kaithal" },
  { label: "Kakinada" },
  { label: "Kancheepuram" },
  { label: "Kannur" },
  { label: "Kanpur" },
  { label: "Kanyakumari" },
  { label: "Karanja" },
  { label: "Karimnagar" },
  { label: "Karnal" },
  { label: "Kasargod" },
  { label: "Kathmandu" },
  { label: "Katni" },
  { label: "Katol" },
  { label: "Khamgaon" },
  { label: "Khammam" },
  { label: "Kochi" },
  { label: "Kolhapur" },
  { label: "Kolkata" },
  { label: "Kollam" },
  { label: "Korba" },
  { label: "Kota" },
  { label: "Kottayam" },
  { label: "Kozhikode" },
  { label: "Kurnool" },
  { label: "Lahore" },
  { label: "Latur" },
  { label: "Lucknow" },
  { label: "Ludhiana" },
  { label: "Madurai" },
  { label: "Malegaon" },
  { label: "Mangalore" },
  { label: "Mapusa" },
  { label: "Margaon" },
  { label: "Medchal" },
  { label: "Meerut" },
  { label: "Mehkar" },
  { label: "Moga" },
  { label: "Mohali" },
  { label: "Mul" },
  { label: "Mumbai" },
  { label: "Murtizapur" },
  { label: "Mysore" },
  { label: "Nagarcoil" },
  { label: "Nagpur" },
  { label: "Nagpur-IT Park (Branch)" },
  { label: "Nagpur-Sadar (Branch)" },
  { label: "Namakkal" },
  { label: "Nanded" },
  { label: "Nandurbar" },
  { label: "Nashik" },
  { label: "Navi Mumbai" },
  { label: "Nellore" },
  { label: "Nizamabad" },
  { label: "Noida" },
  { label: "Osmanabad" },
  { label: "Other" },
  { label: "Palakkad" },
  { label: "Palghar" },
  { label: "Panipat" },
  { label: "Panjim" },
  { label: "Panvel" },
  { label: "Paratwada" },
  { label: "Parbhani" },
  { label: "Pathanamthitta" },
  { label: "Patiala" },
  { label: "Patna" },
  { label: "Pimpri-Chinchwad" },
  { label: "Pokhara" },
  { label: "Pondicherry" },
  { label: "Pulgaon" },
  { label: "Pune" },
  { label: "Pusad" },
  { label: "Raigarh" },
  { label: "Raipur" },
  { label: "Rajahmundry" },
  { label: "Rajkot" },
  { label: "Rajura" },
  { label: "Ramtek" },
  { label: "Ranchi" },
  { label: "Rohtak" },
  { label: "Sagar" },
  { label: "Salem" },
  { label: "Satna" },
  { label: "Secunderabad" },
  { label: "Shegaon" },
  { label: "Silchar" },
  { label: "Sillod" },
  { label: "Sindewahi" },
  { label: "Solapur" },
  { label: "Sri Ganganagar" },
  { label: "Srinagar" },
  { label: "Sultanpur Lodhi" },
  { label: "Surat" },
  { label: "Suryapet" },
  { label: "Thane" },
  { label: "Thiruvananthapuram" },
  { label: "Thrissur" },
  { label: "Tiruchirappalli" },
  { label: "Tirupati" },
  { label: "Trivandrum" },
  { label: "Udaipur" },
  { label: "Ujjain" },
  { label: "Umred" },
  { label: "Umthatha" },
  { label: "Vadodara" },
  { label: "Valsad" },
  { label: "Vapi" },
  { label: "Vellore" },
  { label: "Vijayawada" },
  { label: "Visakhapatnam" },
  { label: "Wani" },
  { label: "Warangal" },
  { label: "Wardha" },
  { label: "Warora" },
  { label: "Warud" },
  { label: "Washim" },
  { label: "Yavatmal" },
];

// Fallback office list for the wizard "Nearest Office" step.
// Used only when Frappe `get_offices_for_b2c` returns empty or fails.
// Refresh manually by re-snapshotting from Frappe `KC Offices`.
export const FALLBACK_NEAREST_OFFICES: WizardOption[] = [
  { label: "Ahmedabad (Navrangpura)" },
  { label: "Amravati" },
  { label: "Amritsar" },
  { label: "Bathinda" },
  { label: "Bengaluru" },
  { label: "Bharuch" },
  { label: "Bhopal" },
  { label: "Calicut" },
  { label: "Ch. Sambhajinagar" },
  { label: "Chandigarh" },
  { label: "Chennai (Velachery)" },
  { label: "Chitradurga" },
  { label: "Coimbatore" },
  { label: "Colombo" },
  { label: "Delhi (Patel Nagar)" },
  { label: "Delhi (Rohini)" },
  { label: "Dhaka" },
  { label: "Ghaziabad" },
  { label: "Goa" },
  { label: "Guntur" },
  { label: "Gurugram (Golf Course Extension Road)" },
  { label: "Gurugram (Sushant Lok)" },
  { label: "Hisar" },
  { label: "Hyderabad (Dilsukhnagar)" },
  { label: "Hyderabad (Himayatnagar)" },
  { label: "Hyderabad (SR Nagar)" },
  { label: "Jabalpur" },
  { label: "Jaipur" },
  { label: "Jalandhar" },
  { label: "Jammu" },
  { label: "Karnal" },
  { label: "Kochi (Pullepady)" },
  { label: "Kollam" },
  { label: "Kottayam" },
  { label: "Ludhiana" },
  { label: "Madurai" },
  { label: "Mysuru" },
  { label: "Nagpur (IT Park)" },
  { label: "Nagpur (Sadar)" },
  { label: "Namakkal" },
  { label: "Nashik" },
  { label: "Noida" },
  { label: "Panipat" },
  { label: "Pathankot" },
  { label: "Patiala" },
  { label: "Puducherry" },
  { label: "Pune (Viman Nagar)" },
  { label: "Raipur" },
  { label: "Rajahmundry" },
  { label: "Rajkot" },
  { label: "Sangrur" },
  { label: "Surat" },
  { label: "Thrissur" },
  { label: "Trivandrum" },
  { label: "Vadodara" },
  { label: "Visakhapatnam" },
  { label: "Visnagar" },
  { label: "Wayanad" },
];

// Fallback icon lookup used only when an option lacks `imageUrl` (e.g. CMS-driven
// entries). Earlier entries win.
export const DEGREE_ICON_MAP: Array<{ match: (option: WizardOption) => boolean; icon: string }> = [
  { match: (option) => option.cfId === 1,  icon: "/icons/degree/workspace_premium.svg" }, // Undergraduate
  { match: (option) => option.cfId === 2,  icon: "/icons/degree/account_balance.svg" },   // Postgraduate
  { match: (option) => option.cfId === 3,  icon: "/icons/degree/cognition_2.svg" },        // PhD
  { match: (option) => option.cfId === 4,  icon: "/icons/degree/description.svg" },        // UG Diploma/Certificate
  { match: (option) => option.cfId === 5,  icon: "/icons/degree/description.svg" },        // PG Diploma/Certificate
  { match: (option) => option.cfId === 8,  icon: "/icons/degree/school.svg" },             // High School (11th-12th)
  { match: (option) => option.cfId === 9,  icon: "/icons/degree/wb_sunny.svg" },           // Short-term/Summer Programs
  { match: (option) => option.cfId === 10, icon: "/icons/degree/school.svg" },             // Grades Below 10th
  { match: (option) => option.cfId === 11, icon: "/icons/degree/bolt.svg" },               // UG+PG (Accelerated) Degree
  { match: (option) => option.cfId === 12, icon: "/icons/degree/partner_exchange_ug.svg" },// Twinning Programs (UG)
  { match: (option) => option.cfId === 13, icon: "/icons/degree/partner_exchange_pg.svg" },// Twinning Programs (PG)
  { match: (option) => option.cfId === 19, icon: "/icons/degree/dictionary.svg" },         // English Language Program
  { match: (option) => option.cfId === 20, icon: "/icons/degree/alt_route_ug.svg" },       // Pathway Programs (UG)
  { match: (option) => option.cfId === 21, icon: "/icons/degree/alt_route_pg.svg" },       // Pathway Programs (PG)
  { match: (option) => option.cfId === 22, icon: "/icons/degree/social_distance.svg" },    // Semester Study Abroad
  { match: (option) => /undergraduate/i.test(option.label),                                icon: "/icons/degree/workspace_premium.svg" },
  { match: (option) => /postgraduate|masters/i.test(option.label),                         icon: "/icons/degree/account_balance.svg" },
  { match: (option) => /\bphd\b/i.test(option.label),                                      icon: "/icons/degree/cognition_2.svg" },
  { match: (option) => /high school|grades below/i.test(option.label),                     icon: "/icons/degree/school.svg" },
  { match: (option) => /ug\+pg|accelerated/i.test(option.label),                           icon: "/icons/degree/bolt.svg" },
  { match: (option) => /short.?term|summer program/i.test(option.label),                   icon: "/icons/degree/wb_sunny.svg" },
  { match: (option) => /pathway/i.test(option.label) && /\(ug\)/i.test(option.label),      icon: "/icons/degree/alt_route_ug.svg" },
  { match: (option) => /pathway/i.test(option.label) && /\(pg\)/i.test(option.label),      icon: "/icons/degree/alt_route_pg.svg" },
  { match: (option) => /pathway/i.test(option.label),                                      icon: "/icons/degree/alt_route_ug.svg" },
  { match: (option) => /semester.*abroad|study abroad/i.test(option.label),                icon: "/icons/degree/social_distance.svg" },
  { match: (option) => /twinning/i.test(option.label) && /\(ug\)/i.test(option.label),     icon: "/icons/degree/partner_exchange_ug.svg" },
  { match: (option) => /twinning/i.test(option.label) && /\(pg\)/i.test(option.label),     icon: "/icons/degree/partner_exchange_pg.svg" },
  { match: (option) => /twinning/i.test(option.label),                                     icon: "/icons/degree/partner_exchange_ug.svg" },
  { match: (option) => /english language/i.test(option.label),                             icon: "/icons/degree/dictionary.svg" },
  { match: (option) => /online|distance/i.test(option.label),                              icon: "/icons/degree/devices.svg" },
  { match: (option) => /hybrid/i.test(option.label),                                       icon: "/icons/degree/split_scene.svg" },
  { match: (option) => /diploma|certificate/i.test(option.label),                          icon: "/icons/degree/description.svg" },
];

